import type { UIEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { useTheme } from 'styled-components';

import { Tooltip } from '@genially/design-system';
import { useResizeObserver } from '@genially/document-components/build/hooks/useResizeObserver';
import type { OpenAnswerInteractiveQuestionItemData } from '@genially/document-model';
import { HideIcon, VisualizationIcon } from '@genially/icons';
import { formatDateDDMMYYYY } from '@genially/utils';

import { UserAliasAvatar } from '../../../../../UserAliasAvatar/UserAliasAvatar';
import { useSidebarSummaryWithContentContext } from '../../SidebarSummaryWithContent.context';
import { Quote } from './components/Quote/Quote';
import { useOpenAnswersSidebarData } from './hooks/useOpenAnswerSidebarData';
import {
  ChildrenMansoryWrapper,
  DataWrapper,
  MasonryWrapper,
  QuestionTitleBasicParagraph,
  QuestionTitleBigParagraph,
  QuestionTitleWrapper,
  TotalParticipantsBigSubtitle,
} from '../../../../SidebarSummary.styled';
import {
  AnswerText,
  AnswerTitle,
  AnswerTitleWrapper,
  OpenAnswerResponse,
  StyledBasicSubtitle,
  StyledSmallParagraph,
  StyledToggleVisibilityIcon,
  UserInfoWrapper,
  UsernameWrapper,
  Wrapper,
} from './OpenAnswerSummary.styled';

interface OpenAnswerSummaryDataProps {
  isFullWidthEnabled?: boolean;
}

const GRADIENT_THRESHOLD = 10;

export const OpenAnswerSummary = ({ isFullWidthEnabled }: OpenAnswerSummaryDataProps) => {
  const {
    selectedQuestion,
    currentUser,
    config: { language, getTranslation, isMobile, toggleOpenAnswerVisibility },
  } = useSidebarSummaryWithContentContext<OpenAnswerInteractiveQuestionItemData>();

  const [hasScrollTop, setHasScrollTop] = useState(false);
  const [hasScrollBottom, setHasScrollBottom] = useState(true);
  const [answerSelected, setAnswerSelected] = useState('');

  const { aggregatedData, totalData } = useOpenAnswersSidebarData();

  const theme = useTheme();

  const handleUnselectAnswer = useCallback(
    event => {
      if (!isMobile) {
        return;
      }

      if (!event.target.closest('[data-answer-selected]')) {
        setAnswerSelected('');
      }
    },
    [isMobile],
  );

  const currentUserData = aggregatedData.find(
    user =>
      user.userAlias === currentUser.alias &&
      ((currentUser.isAuthenticated && user.geniallyUserId) ||
        (!currentUser.isAuthenticated && !user.geniallyUserId)),
  );

  const aggregatedDataOrdered = aggregatedData.sort(item => {
    return item.userAlias === currentUser.alias ? -1 : 0;
  });

  const setGradients = (element: HTMLDivElement) => {
    setHasScrollTop(element.scrollTop >= GRADIENT_THRESHOLD);
    setHasScrollBottom(
      element.scrollHeight - element.scrollTop - element.clientHeight >=
        GRADIENT_THRESHOLD,
    );
  };

  const scrollRef = useResizeObserver(() => {
    if (scrollRef.current) {
      setGradients(scrollRef.current);
    }
  });

  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;

    setGradients(element);
  }, []);

  const getFormattedDateTime = useCallback(
    (dateTimeUTC: string) => {
      const date = new Date(dateTimeUTC);

      const formattedDate = formatDateDDMMYYYY(date);
      const formattedTime = date.toLocaleTimeString(language);

      return `${formattedDate} (${formattedTime})`;
    },
    [language],
  );

  const handleToggleVisibility = useCallback(
    (userAlias: string) => () => {
      toggleOpenAnswerVisibility?.(userAlias);
    },
    [toggleOpenAnswerVisibility],
  );

  const handleSelectAnswer = useCallback(
    (userAlias: string) => (event: React.MouseEvent<Element, MouseEvent>) => {
      if (!isMobile) {
        return;
      }

      if ((event.target as HTMLElement).closest('[data-icon-answer]')) {
        return;
      }

      setAnswerSelected(userAlias === answerSelected ? '' : userAlias);
    },
    [answerSelected, isMobile],
  );

  useEffect(() => {
    document.addEventListener('click', handleUnselectAnswer);

    return () => {
      document.removeEventListener('click', handleUnselectAnswer);
    };
  }, [handleUnselectAnswer]);

  useEffect(() => {
    if (scrollRef.current) {
      setGradients(scrollRef.current);
    }
  }, [scrollRef]);

  const getData = useCallback(() => {
    return aggregatedDataOrdered
      .filter(item => item.answerText)
      .map((item, index) => (
        <OpenAnswerResponse
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          $isSelected={
            (item.userAlias === currentUser.alias &&
              item.geniallyUserId === currentUserData?.geniallyUserId) ||
            (isMobile && item.userAlias === answerSelected)
          }
          $isMobile={isMobile}
          onClick={handleSelectAnswer(item.userAlias)}
          data-answer-selected
        >
          <AnswerTitleWrapper>
            <UserInfoWrapper>
              <UserAliasAvatar name={item.userAlias} />

              <AnswerTitle>
                <UsernameWrapper>
                  <StyledBasicSubtitle $hasSpaces={item.userAlias.includes(' ')}>
                    {item.userAlias}
                  </StyledBasicSubtitle>

                  {!!toggleOpenAnswerVisibility && (
                    <Tooltip
                      text={
                        item.isHidden
                          ? getTranslation(
                              '__new.play.ranking.creator.showOpenAnswer.tooltip',
                            )
                          : getTranslation(
                              '__new.play.ranking.creator.hideOpenAnswer.tooltip',
                            )
                      }
                    >
                      <StyledToggleVisibilityIcon
                        as={item.isHidden ? VisualizationIcon : HideIcon}
                        $show={isMobile && item.userAlias === answerSelected}
                        onClick={handleToggleVisibility(item.userAlias)}
                        data-icon-answer
                      />
                    </Tooltip>
                  )}
                </UsernameWrapper>
                <StyledSmallParagraph>
                  {getFormattedDateTime(item.registeredAt)}
                </StyledSmallParagraph>
              </AnswerTitle>
            </UserInfoWrapper>
            <Quote name={item.userAlias} />
          </AnswerTitleWrapper>
          {!item.isHidden && <AnswerText>{item.answerText}</AnswerText>}
        </OpenAnswerResponse>
      ));
  }, [
    aggregatedDataOrdered,
    currentUser.alias,
    currentUserData?.geniallyUserId,
    isMobile,
    answerSelected,
    handleSelectAnswer,
    toggleOpenAnswerVisibility,
    getTranslation,
    handleToggleVisibility,
    getFormattedDateTime,
  ]);

  const internalChildren = useMemo(() => {
    if (isFullWidthEnabled) {
      return (
        <ChildrenMansoryWrapper
          $hasScrollBottom={hasScrollBottom}
          $hasScrollTop={hasScrollTop}
        >
          <MasonryWrapper ref={scrollRef} onScroll={handleScroll}>
            <ResponsiveMasonry>
              <Masonry gutter={theme.size.units(2)}>{getData()}</Masonry>
            </ResponsiveMasonry>
          </MasonryWrapper>
        </ChildrenMansoryWrapper>
      );
    }

    return <DataWrapper>{getData()}</DataWrapper>;
  }, [
    getData,
    handleScroll,
    hasScrollBottom,
    hasScrollTop,
    isFullWidthEnabled,
    scrollRef,
    theme.size,
  ]);

  const QuestionTitleComponent = isFullWidthEnabled
    ? QuestionTitleBasicParagraph
    : QuestionTitleBigParagraph;

  return (
    <Wrapper $isFullWidthEnabled={isFullWidthEnabled}>
      <TotalParticipantsBigSubtitle>
        {totalData} {getTranslation('__new.content.liveResults.answers')}
      </TotalParticipantsBigSubtitle>

      <QuestionTitleWrapper>
        <QuestionTitleComponent>{selectedQuestion?.Question}</QuestionTitleComponent>
      </QuestionTitleWrapper>

      {internalChildren}
    </Wrapper>
  );
};
